import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiWarningCircleLight,
  PiFilesLight,
  PiListChecksLight,
  PiSparkleLight,
  PiScalesLight,
  PiSealCheckLight,
  PiPlugsConnectedLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Button from '../shared/TailwindComponents/Button';
import { AIProgressPanel, AIRun, useAIRunElapsed } from '../shared/brand/aiProgress';
import { polishMatterMessage } from './matterProgressCopy';

/**
 * Matter creation - the flagship surface for the shared AI progress family.
 *
 * This is the longest-running AI flow in the product and the one people sit
 * and watch, so it renders `AIProgressPanel` directly from a real `AIRun`
 * rather than through the minimal legacy adapter: the centered showcase
 * layout, the run clock, the stage rail as the engine reports it, the
 * engine's optional narration line, and all four terminal states including
 * "finished, but some documents were unreadable".
 *
 * Three deliberate differences from the panel's defaults, all of them about
 * this pipeline specifically:
 *
 *  - **No per-document rows.** `matter_processor` reads the whole batch in
 *    bulk and never reports a single file's progress, so a row per document
 *    sat at "Waiting" for the entire run and took most of the card. The
 *    documents are one quiet count beside the step number instead. Flows whose
 *    engines DO report per-file progress (review, tables, playbooks) keep the
 *    list - it is still the panel's default.
 *  - **No percentage in the header.** The engine's `progress_percent` already
 *    drives the bar, and its own step messages carry a percentage of their
 *    own; printing a second number invited the two to disagree. One bar, one
 *    "Step 3 of 5", no loose number.
 *  - **Showcase density.** A railed stage list, plain glass while the run is
 *    live, and generous spacing - this card IS the screen for several minutes,
 *    so it is laid out like a page, not like a widget in a column.
 *
 * Everything on screen is engine-reported. Nothing is pre-drawn, no
 * percentage is invented, and a stage the engine never mentions never
 * appears.
 */

/**
 * Flavour glyphs for the matter pipeline's own steps, used only while a stage
 * is ACTIVE. A key that isn't here keeps the neutral status glyph - a stage
 * the engine adds tomorrow degrades to the right picture of "working", never
 * to a confidently wrong one.
 */
export const MATTER_STAGE_ICONS: Record<string, React.ReactNode> = {
  connecting: <PiPlugsConnectedLight className="text-champagne" />,
  collecting_context: <PiFilesLight className="text-champagne" />,
  organizing_data: <PiListChecksLight className="text-champagne" />,
  optimizing_data: <PiSparkleLight className="text-champagne" />,
  analysing_legal_sources: <PiScalesLight className="text-champagne" />,
  analyzing_legal_sources: <PiScalesLight className="text-champagne" />,
  generating_relevant_tasks: <PiListChecksLight className="text-champagne" />,
  final_matter_evaluation: <PiSealCheckLight className="text-champagne" />,
};

export interface MatterCreationProgressProps {
  run: AIRun;
  /** Written at submit time, so a remount mid-run resumes the clock. */
  correlationId?: string;
  /** Stops the run and deletes the half-built matter. */
  onCancel?: () => void;
  /** Leaves the surface once the run has failed. */
  onClose?: () => void;
  /**
   * Opens the matter the run created. A finished run navigates there on its
   * own; this is the manual way in, and the only one when the automatic
   * navigation has nowhere to go.
   */
  onOpenMatter?: () => void;
  title?: string;
  completedTitle?: string;
}

const MatterCreationProgress: React.FC<MatterCreationProgressProps> = ({
  run,
  correlationId,
  onCancel,
  onClose,
  onOpenMatter,
  title,
  completedTitle,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  /* The engine's sentences, cleaned up on the way to the screen. Stable so
     the panel's stage rows don't re-render on every clock tick. */
  const polish = React.useCallback(
    (message: string) => polishMatterMessage(message, t),
    [t],
  );

  const elapsedSeconds = useAIRunElapsed(
    run,
    correlationId ? `matter_creation_start:${correlationId}` : undefined,
  );

  const confirmCancel = () => {
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Cancel Analysis'),
        message: t(
          'This will stop the current process and any data created during this process may be deleted.',
        ),
        icon: <PiWarningCircleLight className="w-6 h-6 text-warning-600" />,
        variant: 'danger',
        confirmLabel: t('Cancel Analysis'),
        onConfirm: () => {
          dispatch(hideModal());
          onCancel?.();
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  };

  /*
    What a finished run offers.

    A success normally leaves for the matter by itself, so this button is the
    manual door: the way out of a completion that had no matter id to open,
    and the thing to press if the navigation is ever blocked. A failure keeps
    its Close - leaving is all there is to do with one.
  */
  const finished = run.status === 'completed' || run.status === 'completed_with_errors';
  const footer =
    finished && onOpenMatter ? (
      <Button variant="primary" size="small" onClick={onOpenMatter}>
        {t('Open matter')}
      </Button>
    ) : finished && onClose ? (
      <Button variant="neutral" size="small" onClick={onClose}>
        {t('Back to Matters')}
      </Button>
    ) : (run.status === 'failed' || run.status === 'cancelled') && onClose ? (
      // Leaving is all there is to do with either of these, and without this
      // a run the engine reported as cancelled had no way off the screen.
      <Button variant="neutral" size="small" onClick={onClose}>
        {t('Close')}
      </Button>
    ) : undefined;

  return (
    <AIProgressPanel
      run={{ ...run, elapsedSeconds }}
      layout="centered"
      density="showcase"
      title={title ?? t('Creating matter…')}
      completedTitle={completedTitle ?? t('Matter created')}
      failedTitle={t('Matter creation failed')}
      cancelledTitle={t('Matter creation cancelled')}
      iconForKey={MATTER_STAGE_ICONS}
      showPercent={false}
      formatMessage={polish}
      subItemsDisplay="summary"
      subItemsLabel={
        run.subItems.length === 1
          ? t('1 attached document')
          : t('{{count}} attached documents', { count: run.subItems.length })
      }
      hint={
        <p className="text-sm leading-relaxed text-fg-muted text-center">
          {t(
            'Your matter is being analysed. This usually takes a few minutes — you can safely leave this open.',
          )}
        </p>
      }
      onCancel={onCancel ? confirmCancel : undefined}
      footer={footer}
    />
  );
};

export default MatterCreationProgress;
